import { useEffect, useState, useRef } from 'react'
import { useApi } from '../lib/api'
import type { Flag } from '../types'
import { FlagThumbnail } from '../components/FlagThumbnail'

// ---------------------------------------------------------------------------
// Create / edit form (custom flags only)
// ---------------------------------------------------------------------------
interface FlagFormProps {
  initial?: Partial<Flag>
  onSave: (f: Flag) => void
  onCancel: () => void
}

function FlagForm({ initial, onSave, onCancel }: FlagFormProps) {
  const { apiFetch } = useApi()
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [name, setName] = useState(initial?.name ?? '')
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<string | null>(null)

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] ?? null
    setImageFile(file)
    if (preview) URL.revokeObjectURL(preview)
    setPreview(file ? URL.createObjectURL(file) : null)
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!name.trim()) { setError('Name ist erforderlich'); return }
    if (!initial?.id && !imageFile) { setError('Bild ist erforderlich'); return }

    setSaving(true)
    setError(null)
    try {
      const fd = new FormData()
      fd.append('name', name.trim())
      if (imageFile) fd.append('image', imageFile)

      const url = initial?.id ? `/api/flags/${initial.id}` : '/api/flags'
      const method = initial?.id ? 'PUT' : 'POST'
      const saved = await apiFetch<Flag>(url, { method, body: fd })
      onSave(saved)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Fehler beim Speichern')
    } finally {
      setSaving(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div className="form-group" style={{ marginBottom: 0 }}>
        <label>Name *</label>
        <input
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Name der Flagge"
        />
      </div>
      <div className="form-group" style={{ marginBottom: 0 }}>
        <label>{initial?.id ? 'Neues Bild (optional, ersetzt das vorhandene)' : 'Bild *'}</label>
        <input type="file" accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml" onChange={handleFileChange} />
        {preview && (
          <img
            src={preview}
            alt="Vorschau"
            style={{ marginTop: 8, width: 96, height: 60, objectFit: 'cover', borderRadius: 2, border: '1px solid #e5e7eb' }}
          />
        )}
      </div>
      {error && <div className="error-msg">{error}</div>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? 'Wird gespeichert…' : 'Speichern'}
        </button>
        <button type="button" className="btn btn-secondary" onClick={onCancel}>
          Abbrechen
        </button>
      </div>
    </form>
  )
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function FlagsPage() {
  const { apiFetch } = useApi()
  const [flags, setFlags] = useState<Flag[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState<'' | 'country' | 'custom'>('')
  const [page, setPage] = useState(1)
  const [showForm, setShowForm] = useState(false)
  const [editing, setEditing] = useState<Flag | null>(null)
  const [deleting, setDeleting] = useState<number | null>(null)
  const PAGE_SIZE = 25
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)

  function load(q: string, p: number, type: '' | 'country' | 'custom' = typeFilter) {
    setLoading(true)
    const params = new URLSearchParams({
      limit: String(PAGE_SIZE),
      offset: String((p - 1) * PAGE_SIZE),
    })
    if (q) params.set('q', q)
    if (type) params.set('type', type)
    apiFetch<{ flags: Flag[]; total: number }>(`/api/flags?${params}`)
      .then(({ flags: f, total: t }) => { setFlags(f); setTotal(t) })
      .finally(() => setLoading(false))
  }

  useEffect(() => { load('', 1, '') }, []) // eslint-disable-line react-hooks/exhaustive-deps

  function handleSearchChange(q: string) {
    setSearch(q)
    setPage(1)
    if (searchTimeout.current) clearTimeout(searchTimeout.current)
    searchTimeout.current = setTimeout(() => load(q, 1), 300)
  }

  function handleTypeFilterChange(type: '' | 'country' | 'custom') {
    setTypeFilter(type)
    setPage(1)
    load(search, 1, type)
  }

  async function handleDelete(flag: Flag) {
    if (!window.confirm(`Flagge "${flag.name}" wirklich löschen?`)) return
    setDeleting(flag.id)
    try {
      await apiFetch(`/api/flags/${flag.id}`, { method: 'DELETE' })
      setFlags((prev) => prev.filter((f) => f.id !== flag.id))
      setTotal((t) => t - 1)
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Fehler beim Löschen')
    } finally {
      setDeleting(null)
    }
  }

  function handleSaved(saved: Flag) {
    if (editing) {
      setFlags((prev) => prev.map((f) => (f.id === saved.id ? saved : f)))
    } else {
      setFlags((prev) => [saved, ...prev])
      setTotal((t) => t + 1)
    }
    setShowForm(false)
    setEditing(null)
  }

  const totalPages = Math.ceil(total / PAGE_SIZE)

  return (
    <div>
      <h1 style={{ marginBottom: 20 }}>Flaggen</h1>

      {/* Filter + New */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center' }}>
        <input
          type="search"
          placeholder="Flagge suchen…"
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
          style={{ width: 300 }}
        />
        <select
          value={typeFilter}
          onChange={(e) => handleTypeFilterChange(e.target.value as '' | 'country' | 'custom')}
          style={{ width: 180 }}
        >
          <option value="">Alle Typen</option>
          <option value="country">Länderflaggen</option>
          <option value="custom">Benutzerdefiniert</option>
        </select>
        <button
          className="btn btn-primary"
          onClick={() => { setEditing(null); setShowForm(true) }}
        >
          + Neue Flagge
        </button>
      </div>

      {/* Inline create form */}
      {showForm && !editing && (
        <div className="card" style={{ marginBottom: 16 }}>
          <h3 style={{ marginTop: 0, marginBottom: 16 }}>Neue Flagge erstellen</h3>
          <FlagForm onSave={handleSaved} onCancel={() => setShowForm(false)} />
        </div>
      )}

      {/* Inline edit form */}
      {editing && (
        <div className="card" style={{ marginBottom: 16 }}>
          <h3 style={{ marginTop: 0, marginBottom: 16 }}>Flagge bearbeiten</h3>
          <FlagForm initial={editing} onSave={handleSaved} onCancel={() => setEditing(null)} />
        </div>
      )}

      <div className="card" style={{ padding: 0 }}>
        {loading ? (
          <div className="loading">Flaggen werden geladen…</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th style={{ width: 80 }}>Bild</th>
                <th>Name</th>
                <th style={{ width: 180 }}>Typ</th>
                <th style={{ width: 160 }}>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {flags.length === 0 && (
                <tr>
                  <td colSpan={4} style={{ textAlign: 'center', color: 'var(--color-muted)' }}>
                    Keine Flaggen gefunden
                  </td>
                </tr>
              )}
              {flags.map((f) => (
                <tr key={f.id}>
                  <td style={{ padding: '8px 12px' }}>
                    <FlagThumbnail id={f.id} type={f.type} imageUrl={f.image_url} name={f.name} width={64} height={40} />
                  </td>
                  <td>{f.name}</td>
                  <td>
                    <span className={`badge badge-${f.type === 'country' ? 'completed' : 'design'}`}>
                      {f.type === 'country' ? 'Länderflagge' : 'Benutzerdefiniert'}
                    </span>
                  </td>
                  <td>
                    {f.type === 'custom' && (
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button
                          className="btn btn-secondary"
                          style={{ padding: '4px 10px', fontSize: 13 }}
                          onClick={() => { setShowForm(false); setEditing(f) }}
                        >
                          Bearbeiten
                        </button>
                        <button
                          className="btn btn-secondary"
                          style={{ padding: '4px 10px', fontSize: 13, color: 'var(--color-danger, #dc2626)' }}
                          disabled={deleting === f.id}
                          onClick={() => handleDelete(f)}
                        >
                          {deleting === f.id ? '…' : 'Löschen'}
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 16 }}>
          <button
            className="btn btn-secondary"
            disabled={page === 1}
            onClick={() => { const p = page - 1; setPage(p); load(search, p) }}
          >
            ← Zurück
          </button>
          <span style={{ lineHeight: '32px', color: 'var(--color-muted)' }}>
            Seite {page} von {totalPages}
          </span>
          <button
            className="btn btn-secondary"
            disabled={page === totalPages}
            onClick={() => { const p = page + 1; setPage(p); load(search, p) }}
          >
            Weiter →
          </button>
        </div>
      )}
    </div>
  )
}
